/**
 * BatchActionsPanel Component
 *
 * Renders the batch actions panel that appears when submissions are selected:
 * - Selected count display
 * - Download button
 * - Send email button
 * - Generate/Regenerate button
 *
 * Follows Single Responsibility Principle - only handles batch actions UI
 */

import { Download, Send, RefreshCw } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface BatchActionsPanelProps {
  /** Number of selected submissions */
  selectedCount: number;
  /** Whether any selected submission has a report */
  hasAnyReport: boolean;
  /** Whether generating is in progress */
  isGenerating: boolean;
  /** Callback to open download dialog */
  onDownloadClick: () => void;
  /** Callback to open email dialog */
  onEmailClick: () => void;
  /** Callback to open generate dialog */
  onGenerateClick: () => void;
}

/**
 * Batch actions panel component
 *
 * @example
 * ```tsx
 * {hasSelection && (
 *   <BatchActionsPanel
 *     selectedCount={selectedCount}
 *     hasAnyReport={selectedHaveAnyReport}
 *     isGenerating={isGenerating}
 *     onDownloadClick={() => openDialog("download")}
 *     onEmailClick={() => openDialog("email")}
 *     onGenerateClick={() => openDialog("generate")}
 *   />
 * )}
 * ```
 */
export function BatchActionsPanel({
  selectedCount,
  hasAnyReport,
  isGenerating,
  onDownloadClick,
  onEmailClick,
  onGenerateClick,
}: BatchActionsPanelProps) {
  return (
    <Card className="w-1/2 animate-in slide-in-from-right-5 duration-300">
      <CardContent className="p-4 h-full flex items-center">
        {/* Selected count */}
        <div className="flex-1">
          <p className="text-xs text-muted-foreground">Seleccionados</p>
          <p className="text-2xl font-bold text-primary">{selectedCount}</p>
        </div>

        {/* Action buttons */}
        <div className="flex-1 flex justify-end gap-2">
          {/* Download */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" onClick={onDownloadClick}>
                <Download className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[200px]">
              <p className="font-semibold">Descargar</p>
              <p className="text-xs text-muted-foreground">
                Descarga los reportes en un archivo ZIP
              </p>
            </TooltipContent>
          </Tooltip>

          {/* Send email */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" onClick={onEmailClick}>
                <Send className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[200px]">
              <p className="font-semibold">Enviar por email</p>
              <p className="text-xs text-muted-foreground">
                Envía el reporte a cada participante
              </p>
            </TooltipContent>
          </Tooltip>

          {/* Generate / Regenerate */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="default"
                size="icon"
                onClick={onGenerateClick}
                disabled={isGenerating}
              >
                <RefreshCw
                  className={cn("h-4 w-4", isGenerating && "animate-spin")}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[200px]">
              <p className="font-semibold">
                {hasAnyReport ? "Regenerar" : "Generar"}
              </p>
              <p className="text-xs text-muted-foreground">
                {hasAnyReport
                  ? "Regenera los reportes seleccionados"
                  : "Genera los reportes seleccionados"}
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
      </CardContent>
    </Card>
  );
}
